/**
 * Client-side scope check for per-row buttons ("Edit" on one task, "Delete"
 * on one event). Mirrors the backend scope policies (§3): `owned` = I created
 * it (or own it), `added` = it is assigned to me / I am a member, `both` =
 * either, `all` = any record. The server still enforces it (404 outside the
 * scope) — this only avoids offering an action that would be refused.
 *
 * Unknown scope (legacy cached session) or no signed-in user → false.
 */
import { scopeOf } from './can';
import type { PermissionSlug } from './slugs';
import type { PermissionSnapshot } from './types';

export interface RecordRelation {
  /** users.id of the creator / author / organiser. */
  createdById?: number | null;
  /** users.id of a record owner other than the creator (e.g. an expense's employee user). */
  ownerUserId?: number | null;
  /** users.id of assignees (assignee, second assignee, attendees…). */
  assigneeUserIds?: ReadonlyArray<number | null | undefined>;
  /** I am a member (project member, department writer, team). */
  isMember?: boolean;
}

export function canOnRecord(s: PermissionSnapshot, userId: number | null | undefined, slug: PermissionSlug, record: RecordRelation): boolean {
  const scope = scopeOf(s, slug);
  if (!scope) return false;
  if (scope === 'all') return true;
  if (userId == null) return false;
  const owned = record.createdById === userId || record.ownerUserId === userId;
  const added = !!record.isMember || (record.assigneeUserIds ?? []).some((id) => id === userId);
  if (scope === 'owned') return owned;
  if (scope === 'added') return added;
  return owned || added;
}
